import { memo } from "react";

import CloseCircle from "@zenith/assets/icons/CloseCircle";
import {
  BusinessAddressDTO,
  BusinessDetailsResponseDTO,
  DirectorDetailsStepStatusDTO,
  DirectorStatusDTO,
  DocumentStatusResponseDTO,
  NINVerificationResponseDTO,
  SelfieStatusResponseDTO,
  TierStepsResponseDTO,
} from "@zenith/services/user";

interface StepStatusComponentProps {
  status: string;
  title: string;
  subTitle: string;
  icon: JSX.Element;
  handleDiscardButton: () => void;
  handleContinueButton?: () => void;
  handleTryAgainButton?: () => void;
  errorTitle?: string;
  errorMessage?: string;
  secondErrorMessage?: string;
  rejectedDirectors?: DirectorStatusDTO[];
  showAlternativeAction?: boolean;
  alternativeActionText?: string;
  onAlternativeAction?: () => void;
}

const StepStatusComponent = memo(function StepStatusComponent(
  props: StepStatusComponentProps
) {
  const {
    icon,
    status,
    subTitle,
    title,
    handleDiscardButton,
    rejectedDirectors,
    errorMessage,
    errorTitle,
    handleContinueButton,
    handleTryAgainButton,
    secondErrorMessage,
  } = props;

  function hasPendingStatus(): boolean {
    switch (status) {
      case SelfieStatusResponseDTO.status.PENDING_VERIFICATION:
      case NINVerificationResponseDTO.verificationStatus.PENDING:
      case DocumentStatusResponseDTO.status.PENDING_VERIFICATION:
      case "PENDING":
      case BusinessDetailsResponseDTO.verificationStatus.PENDING:
      case BusinessAddressDTO.verificationStatus.PENDING:
      case DirectorDetailsStepStatusDTO.upgradeTierStepStatus.PENDING:
      case TierStepsResponseDTO.stepStatus.PENDING:
        return true;

      default:
        return false;
    }
  }

  function hasFailedStatus(): boolean {
    switch (status) {
      case SelfieStatusResponseDTO.status.ERROR_IN_VERIFICATION:
      case NINVerificationResponseDTO.verificationStatus.FAILED:
      case DocumentStatusResponseDTO.status.ERROR_IN_VERIFICATION:
      case "FAILED":
      case BusinessDetailsResponseDTO.verificationStatus.FAILED:
      case BusinessAddressDTO.verificationStatus.FAILED:
      case DirectorDetailsStepStatusDTO.upgradeTierStepStatus.REJECTED:
      case TierStepsResponseDTO.stepStatus.REJECTED:
        return true;

      default:
        return false;
    }
  }

  const isPendingStatus = hasPendingStatus();
  const isFailedStatus = hasFailedStatus();

  const showContinueButton =
    handleContinueButton && (isPendingStatus || !isFailedStatus);
  const showTryAgainButton = handleTryAgainButton && isFailedStatus;

  return (
    <div className="mt-6 flex flex-col">
      <div className="ml-2 flex flex-row items-center">
        {icon}
        <h1 className="ml-4 text-2xl font-normal text-neutral-900">{title}</h1>
      </div>
      <p
        className={`${
          isPendingStatus ? "ml-10" : "ml-14"
        } text-sm font-normal text-neutral-600`}
      >
        {subTitle}
      </p>

      {isFailedStatus && (errorTitle || errorMessage || secondErrorMessage) && (
        <>
          <div className="my-8 flex h-0 self-stretch border-t border-neutral-50"></div>
          <div className="flex flex-row">
            <CloseCircle height={"24px"} width={"24px"} />
            <h1 className="ml-4 text-sm font-normal text-neutral-600">
              {errorTitle}
            </h1>
          </div>
          <p className="ml-10 text-sm font-normal text-neutral-600">
            {errorMessage}
          </p>
          <p className="ml-10 text-sm font-normal text-neutral-600">
            {secondErrorMessage}
          </p>
        </>
      )}

      {isFailedStatus && rejectedDirectors && rejectedDirectors?.length > 0 && (
        <>
          <div className="my-8 flex h-0 self-stretch border-t border-neutral-50"></div>
          {rejectedDirectors.map((error, key) => (
            <div className="float-right mb-4 flex flex-col" key={key}>
              <div className="flex flex-row items-center">
                <CloseCircle height={"16px"} width={"16px"} />
                <p className="ml-2 text-sm font-normal text-neutral-600">
                  {error.name}
                </p>
              </div>
              <p className="ml-6 text-sm font-normal text-neutral-600">
                {error.directorNumber}
              </p>
            </div>
          ))}
        </>
      )}

      <div className="stepper-buttons-container !mt-20">
        <button
          id="discard-button"
          className="secondary-button"
          onClick={handleDiscardButton}
          type="button"
        >
          Discard
        </button>

        {showContinueButton && (
          <button
            type="button"
            id="continue-button"
            className="primary-button"
            onClick={handleContinueButton}
          >
            Continue
          </button>
        )}

        {showTryAgainButton && (
          <button
            type="button"
            id="try-again-button"
            className="primary-button"
            onClick={handleTryAgainButton}
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
});

export default StepStatusComponent;
